/**
 * Maps clinical view-model enums to shadcn `Badge` variants and i18n key
 * slugs. Keeps presentation policy out of React components so row layouts
 * stay thin and these rules are unit-testable in isolation.
 */

import type { BadgeProps } from '@/components/ui/badge';
import type {
  CarePathwayStatus,
  ConsentStatus,
  DiagnosisStatus,
  DocumentType,
  FeatureStatus,
  NoteType,
  QuestionnaireStatus,
  TestOrderStatus,
  VariantInterpretation,
} from '@/features/patient-record/types';

export type ShadcnBadgeVariant = NonNullable<NonNullable<BadgeProps['variant']>>;

/** HPO feature presence → badge emphasis (present is strongest signal). */
export function featureStatusToBadgeVariant(
  status: FeatureStatus,
): ShadcnBadgeVariant {
  if (status === 'present') return 'default';
  if (status === 'absent') return 'outline';
  return 'secondary';
}

/** OMIM diagnosis certainty → badge emphasis. */
export function diagnosisStatusToBadgeVariant(
  status: DiagnosisStatus,
): ShadcnBadgeVariant {
  if (status === 'Confirmed' || status === 'Molecular Diagnosis') return 'default';
  if (status === 'Suspected') return 'secondary';
  return 'outline';
}

/**
 * Space-bearing enum value → i18n key segment (`patient.diagnoses.status.*`).
 * New status values from the contract surface as missing-translation
 * warnings in dev instead of falling through to raw text.
 */
export function diagnosisStatusToI18nKey(status: DiagnosisStatus): string {
  return status.replace(/\s+/g, '_').toLowerCase();
}

/** ACMG-style interpretation → badge emphasis for genetic variant rows. */
export function variantInterpretationToBadgeVariant(
  interpretation: VariantInterpretation,
): ShadcnBadgeVariant {
  if (interpretation === 'Pathogenic' || interpretation === 'Likely Pathogenic') {
    return 'destructive';
  }
  if (interpretation === 'VUS') return 'secondary';
  return 'outline';
}

/** Care pathway lifecycle → badge emphasis. */
export function carePathwayStatusToBadgeVariant(
  status: CarePathwayStatus,
): ShadcnBadgeVariant {
  if (status === 'ACTIVE') return 'default';
  if (status === 'COMPLETED') return 'secondary';
  if (status === 'CANCELLED') return 'destructive';
  return 'outline';
}

/** Patient questionnaire delivery state → badge emphasis. */
export function questionnaireStatusToBadgeVariant(
  status: QuestionnaireStatus,
): ShadcnBadgeVariant {
  if (status === 'COMPLETED') return 'default';
  if (status === 'SENT' || status === 'OPENED') return 'secondary';
  if (status === 'EXPIRED' || status === 'REVOKED') return 'destructive';
  return 'outline';
}

/** Lab test order lifecycle → badge emphasis. */
export function testOrderStatusToBadgeVariant(
  status: TestOrderStatus,
): ShadcnBadgeVariant {
  if (status === 'RESULTED') return 'default';
  if (status === 'REVIEWED') return 'secondary';
  if (status === 'SAMPLE_SENT') return 'secondary';
  if (status === 'CANCELLED') return 'destructive';
  return 'outline';
}

/** Consent record state → badge emphasis. */
export function consentStatusToBadgeVariant(
  status: ConsentStatus,
): ShadcnBadgeVariant {
  if (status === 'SIGNED') return 'default';
  if (status === 'PENDING') return 'secondary';
  return 'destructive';
}

/** Note type → badge emphasis. */
export function noteTypeToBadgeVariant(type: NoteType): ShadcnBadgeVariant {
  if (type === 'EMAIL') return 'secondary';
  if (type === 'CALL') return 'outline';
  return 'default';
}

/** Document type → badge emphasis. */
export function documentTypeToBadgeVariant(
  type: DocumentType,
): ShadcnBadgeVariant {
  if (type === 'MEDICAL_REPORT') return 'default';
  if (type === 'QUESTIONNAIRE') return 'secondary';
  if (type === 'IMAGE') return 'secondary';
  return 'outline';
}

